import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Clock3, FileText, Inbox, Leaf, Pencil, UserRound, X } from 'lucide-react';
import { api, type Approval, type Environment } from '../api';
import type { JsonDTO } from '../../../src/domain/json';
import type { NeedsItem } from '../../../src/application/needs-you';
import { Button } from './ui/button';
import { DraftEditor } from './DraftEditor';
import { KnowledgeCited } from './knowledge-cited';
import { EscalationCard } from './escalation-card';
import { actionLabel, actionTone, runBatch, wordDiff, type BatchResult } from '../needs-you';
import { useAgentName } from '../agent-name';
import '../pages/needs-you.css';

/**
 * One item waiting on a person (a draft, a reply or a task) and the decisions on it. Every decision goes through that
 * item's own route with its revision and a fresh idempotency key; nothing here decides on its own.
 */
export type WorkItem = JsonDTO<NeedsItem>;
/** Reply decisions that need no extra input; the rest open in the Inbox, where their fields live. */
const quickReply = new Set(['check_payment', 'open_dispute', 'draft_response', 'delay']);
/** A row's blockers beyond "a person must act", which the row itself already says. */
/** One line per request in a mixed reply. */
const requestLabel: Record<string, string> = { promise: 'Promise to pay', documents: 'Documents', dispute: 'Dispute',
  payment_claim: 'Says it is paid', question: 'Question', other: 'Other' };
export const heldBlockers = (item: Pick<WorkItem, 'blockers'>) => (item.blockers ?? []).filter(b => b.code !== 'needs_review');

/** The nav badge count, refreshed on every committed change. */
export function useNeedsYouCount(signedIn: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!signedIn) return;
    let active = true;
    const load = () => {
      void api<{ total: number }>('/app-api/needs-you/count').then(r => { if (active) setCount(r.total); }).catch(() => {});
    };
    load(); window.addEventListener('alder:state-changed', load);
    return () => { active = false; window.removeEventListener('alder:state-changed', load); };
  }, [signedIn]);
  return count;
}

export interface WorkDecisions {
  busy: boolean; error: string; notice: string;
  /** The approval that can still be undone; `until` is the browser time its window closes. */
  undo: { id: string; revision: number; until: number } | null;
  editing: boolean; setEditing: (on: boolean) => void; showChanges: boolean; setShowChanges: (on: boolean) => void;
  setNotice: (text: string) => void; clear: () => void;
  voted: (item: WorkItem) => boolean;
  decideApproval: (item: WorkItem, kind: 'approve' | 'reject' | 'snooze') => Promise<void>;
  saveEdit: (item: WorkItem, body: string, subject: string) => Promise<void>;
  undoApproval: () => Promise<void>;
  decideTask: (item: WorkItem, action: 'done' | 'snooze' | 'reassign' | 'approve_pause') => Promise<void>;
  decideReply: (item: WorkItem, kind: string) => Promise<void>;
  /** An escalation's reply: have Carol draft it (for approval), then send it as drafted or save an edit, through the approval. */
  draftEscalationReply: (item: WorkItem) => Promise<void>;
  decideEscalationDraft: (item: WorkItem, kind: 'approve' | 'edit', edit?: { body: string; subject: string }) => Promise<void>;
  dismissTask: (item: WorkItem, reason: string) => Promise<void>;
  approveTogether: (items: WorkItem[]) => Promise<BatchResult[] | null>;
}

export function useWorkDecisions({ items, actorId, shadow, environment, reload }: {
  items: readonly WorkItem[]; actorId: string; shadow: boolean; environment: Environment | null; reload: () => Promise<void>;
}): WorkDecisions {
  const [running, setRunning] = useState(0); const [error, setError] = useState(''); const [notice, setNotice] = useState('');
  const [undo, setUndo] = useState<WorkDecisions['undo']>(null);
  const [editing, setEditing] = useState(false); const [showChanges, setShowChanges] = useState(true);
  // One decision at a time, checked synchronously so a double press cannot send two before React re-renders. The lock
  // covers the decision itself; the reload after it only keeps the page busy, so Undo can land while it runs.
  // The lock holds the token of the run that took it, so a run that finishes late never frees someone else's lock.
  const deciding = useRef(0); const tokens = useRef(0);
  // Reloads run one after another, so the last decision's data is always the data left on the page.
  const reloads = useRef<Promise<void>>(Promise.resolve());
  const sequencedReload = () => (reloads.current = reloads.current.catch(() => {}).then(reload));
  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(current => (current === undo ? null : current)), Math.max(0, undo.until - Date.now()));
    return () => clearTimeout(timer);
  }, [undo]);
  async function run<T>(work: () => Promise<T>): Promise<T | null> {
    if (deciding.current) return null;
    const token = ++tokens.current; deciding.current = token;
    const release = () => { if (deciding.current === token) deciding.current = 0; };
    setRunning(n => n + 1); setError(''); setNotice('');
    try {
      const result = await work(); release(); await sequencedReload(); return result;
    } catch (e) { setError(e instanceof Error ? e.message : 'Please try again.'); return null; }
    finally { release(); setRunning(n => n - 1); }
  }
  const undoWindow = (result: Approval) => {
    const left = Date.parse(result.undoUntil ?? '') - Date.parse(result.lastDecisionAt ?? environment?.now ?? '');
    return Number.isFinite(left) && left > 0 ? left : 0;
  };
  function approvedMessage(result: Approval, customer: string) {
    if (result.status === 'pending') return `Your approval for ${customer} is recorded. It needs another approver.`;
    if (shadow) return `Approved for ${customer}. The decision is recorded; nothing is emailed in shadow mode.`;
    return `Approved for ${customer}. The latest facts are checked again just before it sends.`;
  }
  function approval(item: WorkItem, kind: 'approve' | 'reject' | 'snooze' | 'edit', edit?: { body: string; subject: string }) {
    const base = { revision: item.revision, idempotencyKey: crypto.randomUUID(), kind };
    const until = new Date(Date.parse(environment?.now ?? new Date().toISOString()) + 3600_000).toISOString();
    const input = kind === 'edit' ? { ...base, ...edit } : kind === 'snooze' ? { ...base, until } : base;
    return api<Approval>(`/app-api/approvals/${item.id}/decisions`, input);
  }
  const voted = useCallback((item: WorkItem) => Boolean(item.approval?.votes.some(v => v.actorId === actorId)), [actorId]);
  return {
    busy: running > 0, error, notice, undo, editing, setEditing, showChanges, setShowChanges, setNotice, voted,
    clear: () => { setEditing(false); setShowChanges(true); },
    decideApproval: async (item, kind) => { await run(async () => {
      // The window is counted from when the request left, not when the answer came back, so Undo never outlasts it.
      const sent = Date.now();
      const result = await approval(item, kind); setUndo(null);
      if (kind === 'approve') {
        setNotice(approvedMessage(result, item.customerName));
        const left = result.status === 'undo_wait' ? undoWindow(result) : 0;
        if (sent + left > Date.now()) setUndo({ id: item.id, revision: result.revision, until: sent + left });
      }
      if (kind === 'reject') setNotice(`Rejected the draft for ${item.customerName}. Nothing was sent.`);
      if (kind === 'snooze') setNotice(`Snoozed ${item.customerName} for an hour.`);
    }); },
    saveEdit: async (item, body, subject) => { await run(async () => {
      await approval(item, 'edit', { body, subject }); setEditing(false); setShowChanges(true);
      setNotice('Your edit is saved. Approve the new version when it reads right.');
    }); },
    // Undo is offered the moment an approval lands, while its reload is still running, so it does not wait for "busy";
    // it still waits for any decision being sent, and is refused once the window has closed.
    undoApproval: async () => {
      const current = undo;
      if (!current) return;
      if (Date.now() >= current.until) { setUndo(null); setNotice('Too late to undo: it is already on its way.'); return; }
      const target = items.find(i => i.id === current.id) ?? null;
      await run(async () => {
        await api(`/app-api/approvals/${current.id}/decisions`, { revision: current.revision, idempotencyKey: crypto.randomUUID(), kind: 'undo' });
        setUndo(null); setNotice(`Approval undone${target ? ` for ${target.customerName}` : ''}. Nothing was sent.`);
      });
    },
    decideTask: async (item, action) => { await run(async () => {
      await api(`/app-api/tasks/${encodeURIComponent(item.id)}/decisions`, { action, revision: item.revision,
        idempotencyKey: crypto.randomUUID() });
      setNotice(action === 'snooze' ? 'Snoozed to the next business day.' : action === 'reassign' ? 'Handed to the other team.'
        : action === 'approve_pause' ? `Reminders to ${item.customerName} are paused.` : 'Marked done.');
    }); },
    decideReply: async (item, kind) => { await run(async () => {
      const result = await api<{ result?: { error?: string } }>(`/app-api/replies/${item.id}/decisions`,
        { kind, revision: item.revision, idempotencyKey: crypto.randomUUID(), policyId: 'aiprise' });
      if (result.result?.error) throw new Error(result.result.error);
      setNotice(kind === 'draft_response' ? 'A reply is drafted. It is waiting for your approval.' : `${actionLabel(kind)}: recorded.`);
    }); },
    draftEscalationReply: async item => { await run(async () => {
      await api(`/app-api/tasks/${encodeURIComponent(item.id)}/reply-draft`, {});
      setNotice(`The reply to ${item.customerName} is drafted. Nothing is sent until it is approved.`);
    }); },
    decideEscalationDraft: async (item, kind, edit) => { await run(async () => {
      const draft = item.escalation?.draft;
      if (draft?.source !== 'approval') throw new Error('There is no draft to decide on yet.');
      const sent = Date.now();
      const result = await api<Approval>(`/app-api/approvals/${draft.approvalId}/decisions`,
        { revision: draft.revision, idempotencyKey: crypto.randomUUID(), kind, ...(kind === 'edit' ? edit : {}) });
      const left = kind === 'approve' && result.status === 'undo_wait' ? undoWindow(result) : 0;
      if (sent + left > Date.now()) setUndo({ id: draft.approvalId, revision: result.revision, until: sent + left });
      setNotice(kind === 'edit' ? 'Your edit is saved. Send it when it reads right.' : approvedMessage(result, item.customerName));
    }); },
    dismissTask: async (item, reason) => { await run(async () => {
      await api(`/app-api/tasks/${encodeURIComponent(item.id)}/decisions`, { action: 'dismiss', reason, revision: item.revision,
        idempotencyKey: crypto.randomUUID() });
      setNotice('Dismissed. Your reason is kept so Carol learns from it.');
    }); },
    approveTogether: items => run(async () => {
      const outcome = await runBatch(items, async item => approvedMessage(await approval(item, 'approve'), item.customerName));
      setUndo(null);
      setNotice(`${outcome.filter(r => r.ok).length} of ${outcome.length} approved.`);
      return outcome.map(r => ({ ...r, message: r.ok ? r.message : `${items.find(i => i.key === r.key)?.customerName}: ${r.message}` }));
    }),
  };
}

export function AmyNote({ item }: { item: WorkItem }) {
  const agent = useAgentName();
  const r = item.recommendation;
  return <div className="ny-amy" role="note" aria-label={`${agent}'s recommendation`}>
    <span className="ny-amy-mark" aria-hidden><Leaf size={15} strokeWidth={1.8} /></span>
    <div><p className="ny-amy-lead">{agent} recommends
      <span className={`ny-chip tone-${actionTone(r.action)}`}>{actionLabel(r.action)}</span></p>
      <p className="ny-amy-reason">{r.reason}</p></div>
  </div>;
}
function Why({ item }: { item: WorkItem }) {
  if (!item.why.length) return null;
  return <details className="ny-why" open={heldBlockers(item).length > 0}>
    <summary>{heldBlockers(item).length ? 'Why this is held' : 'Why?'}</summary>
    <ul>{item.why.map(line => <li key={line}>{line}</li>)}</ul>
  </details>;
}
function Diff({ before, after, label }: { before: string; after: string; label: string }) {
  return <div className="ny-diff" aria-label={label}>{wordDiff(before, after).map((part, index) => part.kind === 'same'
    ? <span key={index}>{part.text}</span> : part.kind === 'add' ? <ins key={index}>{part.text}</ins>
      : <del key={index}>{part.text}</del>)}</div>;
}

/** Amy's recommendation, then the item itself (the email, the reply or the task) with its decisions. */
export function WorkItemBody({ item, work, canDecide }: { item: WorkItem; work: WorkDecisions; canDecide: boolean }) {
  return <>
    <AmyNote item={item} />
    {item.escalation && <EscalationCard item={item} work={work} canDecide={canDecide} />}
    {item.kind === 'approval' ? <ApprovalBody item={item} work={work} canDecide={canDecide} />
      : item.kind === 'reply' ? <ReplyBody item={item} work={work} canDecide={canDecide} />
        : <TaskBody item={item} work={work} canDecide={canDecide} />}
  </>;
}

function ApprovalBody({ item, work, canDecide }: { item: WorkItem; work: WorkDecisions; canDecide: boolean }) {
  const agent = useAgentName();
  const draft = item.approval!.draft; const original = item.original;
  const pending = item.status === 'pending'; const { busy, editing, showChanges } = work;
  const invoiceNumber = (id: string) => draft.factsSnapshot.invoices.find(i => i.id === id)?.number ?? 'Invoice';
  return <>
    <div className="ny-email">
      <div className="ny-address"><span>To</span><strong>{draft.to.join(', ')}</strong>{draft.cc.length > 0 && <><span>Cc</span>
        <strong>{draft.cc.join(', ')}</strong></>}<span>From</span><strong>{draft.from.name}</strong></div>
      {editing ? <DraftEditor key={`${item.id}:${item.revision}`} subject={draft.subject} body={draft.body} busy={busy}
        onSave={(body, subject) => void work.saveEdit(item, body, subject)} onCancel={() => work.setEditing(false)} />
        : original && showChanges
          ? <div className="ny-changes"><p className="ny-legend"><ins>Your words</ins><del>{agent}&rsquo;s draft</del></p>
            {original.subject !== draft.subject && <Diff before={original.subject} after={draft.subject} label="Subject changes" />}
            <Diff before={original.body} after={draft.body} label="Body changes" /></div>
          : <><h3 className="ny-subject">{draft.subject}</h3>
            <div className="ny-body">{draft.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}</div></>}
      {original && !editing && <Button className="ny-toggle" variant="ghost" size="sm" onClick={() => work.setShowChanges(!showChanges)}>
        {showChanges ? 'Show the email as it will send' : 'Show what you changed'}</Button>}
      {draft.attachments.length > 0 && <ul className="ny-attachments" aria-label="Attachments">{draft.attachments.map(a =>
        <li key={a.invoiceId}><FileText size={15} aria-hidden />{invoiceNumber(a.invoiceId)}.pdf
          {a.notice && <small>{a.notice}</small>}</li>)}</ul>}
      <KnowledgeCited cited={draft.provenance.knowledge} />
    </div>
    <Why item={item} />
    {!editing && <footer className="ny-actions">{canDecide ? <>
      <Button variant="ghost" disabled={busy} onClick={() => void work.decideApproval(item, 'reject')}><X aria-hidden />Reject</Button>
      {pending && <Button variant="outline" disabled={busy} onClick={() => void work.decideApproval(item, 'snooze')}>
        <Clock3 aria-hidden />Snooze 1h</Button>}
      <Button variant="outline" disabled={busy} onClick={() => work.setEditing(true)}><Pencil aria-hidden />Edit</Button>
      {pending && <Button disabled={busy || work.voted(item)} onClick={() => void work.decideApproval(item, 'approve')}><Check aria-hidden />
        {work.voted(item) ? 'Your approval is recorded' : 'Approve'}</Button>}
      {!pending && <span className="ny-muted">Snoozed. It comes back to you when the snooze ends.</span>}
    </> : <span className="ny-muted">An approver can act on this draft.</span>}</footer>}
  </>;
}

function ReplyBody({ item, work, canDecide }: { item: WorkItem; work: WorkDecisions; canDecide: boolean }) {
  const reply = item.reply!; const action = item.recommendation.action;
  return <>
    <div className="ny-email">
      <div className="ny-address"><span>From</span><strong>{reply.from}</strong><span>Subject</span><strong>{reply.subject}</strong></div>
      <blockquote className="ny-quote">{reply.text}</blockquote>
      {reply.requests && <ul className="ny-requests" aria-label="What they ask for">{reply.requests.map((r, index) =>
        <li key={index}><strong>{requestLabel[r.type] ?? r.type}{r.invoices.length ? ` (${r.invoices.join(', ')})` : ''}</strong>
          {' '}<q>{r.quote}</q></li>)}</ul>}
    </div>
    <Why item={item} />
    <footer className="ny-actions">
      <a className="ny-link" href={`#inbox/${item.id}`}><Inbox size={15} aria-hidden />Open in Inbox for every option</a>
      {canDecide && quickReply.has(action) && item.customerId && !reply.securityFlag && reply.kind !== 'injection' &&
        <Button disabled={work.busy} onClick={() => void work.decideReply(item, action)}><Check aria-hidden />{actionLabel(action)}</Button>}
    </footer>
  </>;
}

function TaskBody({ item, work, canDecide }: { item: WorkItem; work: WorkDecisions; canDecide: boolean }) {
  const pause = item.task?.kind === 'pause_request'; const { busy } = work;
  return <>
    {!item.escalation && <div className="ny-email"><p className="ny-task">{item.task?.title}</p></div>}
    <Why item={item} />
    <footer className="ny-actions">{canDecide ? <>
      <Button variant="ghost" disabled={busy} onClick={() => void work.decideTask(item, 'reassign')}><UserRound aria-hidden />Hand off</Button>
      <Button variant="outline" disabled={busy} onClick={() => void work.decideTask(item, 'snooze')}>
        <Clock3 aria-hidden />Snooze a day</Button>
      <Button disabled={busy} onClick={() => void work.decideTask(item, pause ? 'approve_pause' : 'done')}><Check aria-hidden />
        {pause ? 'Approve pause' : 'Mark done'}</Button>
    </> : <span className="ny-muted">An approver can act on this task.</span>}</footer>
  </>;
}
